import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Chip,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../axios';
import ModuleHeader from '../components/ModuleHeader';
import { errorMsg } from '../utils/errorMsg';

export default function AreaObreiros({ onLogout }) {
  const [loading, setLoading] = useState(true);
  const [obreiros, setObreiros] = useState([]);
  const [membros, setMembros] = useState([]);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({
    nome: '',
    email: '',
    telefone: '',
    cargo: '',
    data_ordenacao: '',
    status: 'ativo',
    observacoes: '',
    membro_id: null,
  });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [obRes, memRes] = await Promise.all([
        api.get('/obreiros'),
        api.get('/secretaria/membros'),
      ]);
      setObreiros(obRes.data || []);
      setMembros(memRes.data || []);
    } catch (err) {
      setError(errorMsg(err) || 'Erro ao buscar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const abrirNovo = () => {
    setEditando(null);
    setForm({
      nome: '',
      email: '',
      telefone: '',
      cargo: '',
      data_ordenacao: '',
      status: 'ativo',
      observacoes: '',
      membro_id: null,
    });
    setErro('');
    setOpen(true);
  };

  const abrirEditar = (o) => {
    setEditando(o);
    setForm({
      nome: o.nome,
      email: o.email || '',
      telefone: o.telefone || '',
      cargo: o.cargo || '',
      data_ordenacao: o.data_ordenacao || '',
      status: o.status || 'ativo',
      observacoes: o.observacoes || '',
      membro_id: o.membro_id || null,
    });
    setErro('');
    setOpen(true);
  };

  const salvar = async () => {
    if (!form.nome.trim()) {
      setErro('Nome é obrigatório.');
      return;
    }
    if (form.status === 'ativo' && !form.membro_id) {
      setErro('Um obreiro ativo deve estar vinculado a um membro oficialmente registrado da instituição.');
      return;
    }
    setSalvando(true);
    setErro('');
    try {
      if (editando) {
        await api.put(`/obreiros/${editando.id}`, form);
      } else {
        await api.post('/obreiros', form);
      }
      setOpen(false);
      carregar();
    } catch (e) {
      setErro(e?.response?.data?.detail || 'Erro ao salvar.');
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (o) => {
    if (!window.confirm(`Excluir obreiro "${o.nome}"?`)) return;
    try {
      await api.delete(`/obreiros/${o.id}`);
      carregar();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Erro ao excluir.');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
      <ModuleHeader module="Secretaria" title="Obreiros" />

      {/* Resumo rápido */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="caption">
                Obreiros cadastrados
              </Typography>
              <Typography variant="h4" color="primary.main" sx={{ fontSize: '4.4rem', fontWeight: 800, lineHeight: 1 }}>
                {obreiros.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="caption">
                Ativos
              </Typography>
              <Typography variant="h4" color="success.main" sx={{ fontSize: '4.4rem', fontWeight: 800, lineHeight: 1 }}>
                {obreiros.filter((o) => o.status === 'ativo').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Botão novo */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNovo} sx={{ flexShrink: 0 }}>
          Novo obreiro
        </Button>
      </Box>

      {/* Tabela */}
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Nome</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Membro</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Cargo</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Telefone</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Ordenação</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {obreiros.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Nenhum obreiro cadastrado.
                  </TableCell>
                </TableRow>
              )}
              {obreiros.map((o) => {
                const membro = membros.find((m) => m.id === o.membro_id);
                return (
                  <TableRow key={o.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{o.nome}</TableCell>
                    <TableCell>{membro ? membro.nome : '—'}</TableCell>
                    <TableCell>{o.cargo || '—'}</TableCell>
                    <TableCell>{o.telefone || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={o.status === 'ativo' ? 'Ativo' : 'Inativo'}
                        color={o.status === 'ativo' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {o.data_ordenacao
                        ? new Date(o.data_ordenacao + 'T00:00:00').toLocaleDateString('pt-BR')
                        : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => abrirEditar(o)} aria-label="Editar">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => excluir(o)}
                          aria-label="Excluir"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog para criar/editar */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editando ? 'Editar obreiro' : 'Novo obreiro'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          {erro && <Alert severity="error">{erro}</Alert>}
          <TextField
            label="Nome *"
            value={form.nome}
            onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Telefone"
            value={form.telefone}
            onChange={(e) => setForm((f) => ({ ...f, telefone: e.target.value }))}
            fullWidth
          />
          <TextField
            label="E-mail"
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Cargo"
            value={form.cargo}
            onChange={(e) => setForm((f) => ({ ...f, cargo: e.target.value }))}
            fullWidth
            placeholder="ex.: Diácono, Presbítero, Pastor, Evangelista"
          />
          <TextField
            label="Data de Ordenação"
            type="date"
            value={form.data_ordenacao}
            onChange={(e) => setForm((f) => ({ ...f, data_ordenacao: e.target.value }))}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select
            label="Status"
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            fullWidth
          >
            <MenuItem value="ativo">Ativo</MenuItem>
            <MenuItem value="inativo">Inativo</MenuItem>
          </TextField>
          {form.status === 'ativo' && (
            <TextField
              select
              label="Membro *"
              value={form.membro_id || ''}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  membro_id: e.target.value ? Number(e.target.value) : null,
                }))
              }
              fullWidth
              error={!form.membro_id}
              helperText={!form.membro_id ? 'Selecione um membro para ativar o obreiro' : ''}
            >
              <MenuItem value="">Selecione um membro</MenuItem>
              {(membros || []).map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.nome}
                </MenuItem>
              ))}
            </TextField>
          )}
          {form.status === 'inativo' && (
            <TextField
              select
              label="Membro"
              value={form.membro_id || ''}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  membro_id: e.target.value ? Number(e.target.value) : null,
                }))
              }
              fullWidth
            >
              <MenuItem value="">Nenhum</MenuItem>
              {(membros || []).map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.nome}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            label="Observações"
            value={form.observacoes}
            onChange={(e) => setForm((f) => ({ ...f, observacoes: e.target.value }))}
            fullWidth
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={salvar} disabled={salvando}>
            {salvando ? <CircularProgress size={18} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
